
import { z } from "zod";



// CHANGED BLOCK TRACKING — the client side of the SnapDiff-equivalent surface
// (M4a, 2026-08-04; docs/cbt-snapdiff-design-2026-08-04.md, contract
// volumes.yaml `actionDiff`).
//
// `POST /api/v1/volumes/{v}/actions/diff` is a QUERY riding the task engine's
// LEASE-FREE channel: it answers 202 + X-TVStor-Task and the answer comes back
// on TWO channels, both of which this module decodes.
//
//   1. `Task.result` — the NORMALIZED SUMMARY plus the first N extents inline
//      (N is a backend knob, default 10 000). `truncated` says whether the
//      inline list is short; `extentCount` is ALWAYS the real total.
//   2. The task LOG (`GET /tasks/{task}/log`, NDJSON) — the FULL, authoritative
//      extent list, one frame per extent, always written. `?follow=true` lets a
//      client consume extents while the diff still runs.
//
// Everything here is engine-neutral BY CONSTRUCTION: a `thin_delta` answer and
// an `rbd diff` answer are indistinguishable at this layer, which is the whole
// point of the action (architecture §5.2, "the engine is a datapool detail").
// Units are BYTES from the start of the volume — never blocks, sectors or
// chunks; `granularityBytes` is metadata ABOUT the extents (how coarse the
// truth underneath is), never the unit they are expressed in.

export interface DiffExtent {
    offset: number;
    length: number;
}



// The two ends of a comparison as the ANSWER renders them: a snapshot id, or
// the live volume (`to: "head"` in the request body).
export interface DiffSnapshotEndpoint {
    snapshot: string;
}

export interface DiffHeadEndpoint {
    head: true;
}

export type DiffEndpoint = DiffSnapshotEndpoint | DiffHeadEndpoint;



export function isDiffHeadEndpoint(endpoint: DiffEndpoint): endpoint is DiffHeadEndpoint {
    return "head" in endpoint;
}



// The body of the action. `from` is a snapshot ID (UUIDv7 — the CLI resolves
// names, the API takes ids); `to` is another snapshot id or the ONE literal
// `head`. `format` ships as `extents` only in M4a — the binary `stream` form is
// the next round (dated seam 2026-08-04, contract `VolumeDiffRequest`), so the
// field is deliberately absent from this input type rather than typed and
// refused.
export interface VolumeDiffInput {
    from: string;
    // A snapshot id, or the literal `head` — one string field, because that is
    // exactly what the contract's `VolumeDiffRequest.to` is.
    to: string;
}



// The parsed `Task.result`. ONE deviation from the wire shape, dated
// 2026-08-04 and deliberate: `truncated` is ALWAYS present here as a boolean
// (the wire omits it when false, the usual JSON economy) — a consumer of a
// typed client should never have to write `x.truncated === true` to ask a
// yes/no question.
export interface VolumeDiffResult {
    granularityBytes: number;
    from: DiffEndpoint;
    to: DiffEndpoint;
    extents: DiffExtent[];
    extentCount: number;
    truncated: boolean;
}



const zDiffExtent = z.object({
    offset: z.number().int().nonnegative(),
    length: z.number().int().nonnegative(),
});

const zDiffEndpoint = z.union([
    z.object({ snapshot: z.string() }),
    z.object({ head: z.literal(true) }),
]);

// `Task.result` is free-form on the wire (contract tasks.yaml:
// `additionalProperties: true` — that IS the query channel), so the diff's
// shape is validated HERE, at the one place that knows what a diff answer looks
// like. A malformed result is a backend bug and throws rather than degrading:
// silently handing a backup product half an extent list is the one failure mode
// this surface must never have.
const zVolumeDiffResultWire = z.object({
    granularityBytes: z.number().int().positive(),
    from: zDiffEndpoint,
    to: zDiffEndpoint,
    extents: z.array(zDiffExtent),
    extentCount: z.number().int().nonnegative(),
    truncated: z.boolean().optional(),
});



export function parseVolumeDiffResult(value: unknown): VolumeDiffResult {
    const wire = zVolumeDiffResultWire.parse(value);
    return {
        granularityBytes: wire.granularityBytes,
        from: wire.from,
        to: wire.to,
        extents: wire.extents,
        extentCount: wire.extentCount,
        truncated: wire.truncated ?? false,
    };
}



// The number of bytes a backup has to copy. Deliberately NOT a field of the
// result: over a TRUNCATED answer the inline extents do not sum to the truth,
// so the honest total comes either from the log channel's header line or from
// summing the FULL list — this helper is for whichever one the caller holds.
export function changedBytesOf(extents: readonly DiffExtent[]): number {
    return extents.reduce((total, extent) => total + extent.length, 0);
}



// ---- The log channel's line protocol ----
// Task log frames are `{ts, level, message}` with `message` a STRING (contract
// tasks.yaml), so a structured extent is a JSON document INSIDE that string,
// interleaved with the ordinary human lines of the same task. This reader is
// LENIENT by construction: anything it does not recognize is null and the
// caller skips it — a human line, a warning, a line kind this client predates.
export type DiffLogLine =
    | { kind: "header"; header: Record<string, unknown> }
    | { kind: "extent"; extent: DiffExtent };



function extentFrom(offset: unknown, length: unknown): DiffExtent | null {
    if (typeof offset !== "number" || typeof length !== "number") {
        return null;
    }
    if (!Number.isSafeInteger(offset) || !Number.isSafeInteger(length) || offset < 0 || length < 0) {
        return null;
    }
    return { offset, length };
}



// TWO accepted spellings, on purpose (deviation recorded 2026-08-04): the
// contract's operation description documents the flat
// `{"kind":"extent","offset":…,"length":…}` frame, while the executor-side
// writer emits the nested `{"extent":{"offset":…,"length":…}}` form. A CLIENT
// is the wrong place to arbitrate that: it reads both and keeps working
// whichever one the backend settles on.
export function parseDiffLogMessage(message: string): DiffLogLine | null {
    const trimmed = message.trim();
    if (!trimmed.startsWith("{")) {
        return null;
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(trimmed);
    }
    catch {
        return null;
    }
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return null;
    }
    const root = parsed as Record<string, unknown>;

    // Nested form.
    const nested = root["extent"];
    if (typeof nested === "object" && nested !== null && !Array.isArray(nested)) {
        const fields = nested as Record<string, unknown>;
        const extent = extentFrom(fields["offset"], fields["length"]);
        return extent !== null ? { kind: "extent", extent } : null;
    }

    // Flat, discriminated form.
    if (root["kind"] === "extent") {
        const extent = extentFrom(root["offset"], root["length"]);
        return extent !== null ? { kind: "extent", extent } : null;
    }

    // The header line, written once before the extents so a streaming client
    // knows the granularity and the total before the first extent arrives.
    const header = root["diff"];
    if (typeof header === "object" && header !== null && !Array.isArray(header)) {
        return { kind: "header", header: header as Record<string, unknown> };
    }
    if (root["kind"] === "diff") {
        return { kind: "header", header: root };
    }
    return null;
}



// The task-log frame stream (as `tasks.follow`/`tasks.log` yield it) filtered
// down to the extents it carries — the FULL, authoritative list. Streams: it
// never materializes the delta, which is the point of the log channel existing
// at all.
export async function* diffExtentsOfFrames(frames: AsyncIterable<Record<string, unknown>>): AsyncGenerator<DiffExtent> {
    for await (const frame of frames) {
        const message = frame["message"];
        if (typeof message !== "string") {
            continue;
        }
        const line = parseDiffLogMessage(message);
        if (line !== null && line.kind === "extent") {
            yield line.extent;
        }
    }
}



// How long the reader keeps re-opening a log that ends SHORT, and how many
// consecutive re-reads may bring nothing new before it gives up.
const LOG_RETRY_DELAY_MS = 1_000;
const LOG_STALL_LIMIT = 5;
const LOG_ATTEMPT_LIMIT = 200;



// THE COMPLETENESS GUARD ON THE LOG CHANNEL (fix 2026-08-08). The header of
// this file promises that "silently handing a backup product half an extent
// list is the one failure mode this surface must never have" — and the guard
// existed only on the `Task.result` channel (the zod parse above). Nothing
// compared the number of extents drained from the NDJSON log against the
// authoritative `extentCount`, while the transport has two ways of ending a
// SHORT log with a perfectly clean EOF:
//   - the backend catches a store that died mid-stream and calls
//     `reply.raw.end()` on the already-hijacked 200 (tasks/routes.ts) — undici
//     sees a normal end of body;
//   - task logs are LOCAL-first and reach the store behind the uploader's
//     high-water mark, so a task that is already terminal can still have rows
//     in flight; the follow loop stops at "terminal" and hands over what the
//     store holds SO FAR.
// Both used to return a truncated list with `truncated: false` and an
// under-counted `changed:` — a backup script copying less than the delta.
// The reader now knows the total and, when the stream ends short, does exactly
// what the backend's own comment presumes a client does: RE-FOLLOWS, skipping
// the prefix it already yielded (the log is append-only and stably ordered, so
// a re-read is the same list plus whatever landed since). It keeps going while
// each attempt makes progress — which is how it rides out the uploader's lag —
// and throws a speaking, retryable error once nothing new arrives.
export async function* diffExtentsOfLog(
    open: () => AsyncIterable<Record<string, unknown>>,
    expectedCount: number | null,
): AsyncGenerator<DiffExtent> {
    let expected = expectedCount;
    let yielded = 0;
    let stalled = 0;

    for (let attempt = 0; attempt < LOG_ATTEMPT_LIMIT; attempt += 1) {
        const before = yielded;
        let seen = 0;
        for await (const frame of open()) {
            const message = frame["message"];
            if (typeof message !== "string") {
                continue;
            }
            const line = parseDiffLogMessage(message);
            if (line === null) {
                continue;
            }
            if (line.kind === "header") {
                // The total the executor itself declared, for a caller that
                // holds no summary.
                const declared = line.header["extentCount"];
                if (expected === null && typeof declared === "number" && Number.isInteger(declared) && declared >= 0) {
                    expected = declared;
                }
                continue;
            }
            seen += 1;
            if (seen > yielded) {
                yielded = seen;
                yield line.extent;
            }
        }

        if (expected === null || yielded >= expected) {
            // Nothing declared the total (an old task whose header predates
            // this line, say): the caller gets what the log carries, exactly
            // as before — inventing a failure would be its own lie.
            return;
        }
        stalled = yielded > before ? 0 : stalled + 1;
        if (stalled >= LOG_STALL_LIMIT) {
            break;
        }
        await new Promise<void>((resolve) => {
            setTimeout(resolve, LOG_RETRY_DELAY_MS);
        });
    }

    throw new Error(
        `incomplete diff log: ${String(yielded)} of ${String(expected ?? 0)} extent(s) — the log stream ended early, re-run the read`,
    );
}

